/**
 * One-shot bootstrap: scan `window.location.search` for any
 * `ff.*` parameters and persist them into `localStorage`.
 *
 * Must run BEFORE TanStack Router mounts. Most portal routes use
 * `validateSearch` to whitelist known query params; anything else
 * (including our feature-flag overrides) is stripped from the URL
 * the moment the router resolves the route. By capturing into
 * localStorage at bootstrap, we make the URL-param mechanism
 * survive that stripping.
 *
 * After this runs, `isFeatureEnabled` reads localStorage as usual
 * and finds the value the URL was attempting to set. The user's
 * URL still ends up clean (router strips the param), which is what
 * we want — the URL-param mechanism is "set once via URL, persist
 * in this browser thereafter."
 *
 * Idempotent: calling multiple times is safe. Same-tab refresh
 * without the URL param does not clear the previously-captured
 * value (that requires an explicit `?ff.X=0` or
 * `localStorage.removeItem`).
 */

import { STORAGE_PREFIX, URL_PREFIX } from "./index";

export function captureFeatureFlagsFromUrl(): void {
  if (typeof window === "undefined") return;
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(window.location.search);
  } catch {
    return;
  }
  for (const [key, value] of params.entries()) {
    if (!key.startsWith(URL_PREFIX)) continue;
    // Strip the URL_PREFIX and re-add the STORAGE_PREFIX so a future
    // divergence (e.g. different prefix for org-scoped flags) routes
    // through the shared constants and not a hardcoded "ff.".
    const flagName = key.slice(URL_PREFIX.length);
    const storageKey = `${STORAGE_PREFIX}${flagName}`;
    try {
      window.localStorage.setItem(storageKey, value);
    } catch {
      /* private mode / quota — ignore */
    }
  }
}
